import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FormBuilder,
  scrollToFirstFormError,
} from '../../../components/ui';
import {
  getUserFormFieldOrder,
  getUserFormSections,
} from '../config/userFormConfig';
import UserOrganisationSearchableSelect from './UserOrganisationSearchableSelect';
import UserProfileSearchableSelect from './UserProfileSearchableSelect';
import {
  defaultIsOptionEqualToValue,
  injectSelectedOption,
} from '../../../components/ui/SearchableSelect/selectUtils';

export default function UserForm({
  formData,
  errors,
  updateField,
  validationAttempt = 0,
  profileOptions = [],
  profileLoading = false,
  onLoadMoreProfile,
  onSelectOrganisation,
  onProfileChange,
  disabled = false,
  showOrganisationField = true,
  disableProfileChange = false,
  profileHelperText,
}) {
  const sections = useMemo(
    () => getUserFormSections({ showOrganisationField }),
    [showOrganisationField]
  );
  const fieldOrder = useMemo(() => getUserFormFieldOrder(sections), [sections]);

  useEffect(() => {
    if (validationAttempt > 0) {
      scrollToFirstFormError(errors, fieldOrder);
    }
  }, [errors, fieldOrder, validationAttempt]);

  const renderUserField = (field, ctx) => {
    const { name, label, required } = field;
    const errorMessage = ctx.errors[name];

    switch (name) {
      case 'organisation':
        return (
          <UserOrganisationSearchableSelect
            value={ctx.values.organisation || null}
            onChange={(option) => onSelectOrganisation?.(option)}
            label={label}
            required={required}
            disabled={disabled || ctx.disabled}
            error={Boolean(errorMessage)}
            errorMessage={errorMessage}
          />
        );
      case 'profile': {
        const profileOptionsWithSelected = injectSelectedOption(
          profileOptions,
          ctx.values.profile,
          (option, selected) => defaultIsOptionEqualToValue(option, selected, 'id')
        );
        return (
          <UserProfileSearchableSelect
            options={
              disableProfileChange && ctx.values.profile
                ? [ctx.values.profile]
                : profileOptionsWithSelected
            }
            value={ctx.values.profile || null}
            onChange={(profile) => onProfileChange?.(profile)}
            label={label}
            loading={disableProfileChange ? false : profileLoading}
            disabled={disabled || ctx.disabled || disableProfileChange}
            error={Boolean(errorMessage)}
            errorMessage={errorMessage || (disableProfileChange ? profileHelperText : '')}
            onLoadMore={disableProfileChange ? undefined : onLoadMoreProfile}
          />
        );
      }
      default:
        return null;
    }
  };

  const handleFieldChange = (name, value) => {
    if (name === 'mobile') {
      const digitsOnly = String(value || '').replace(/\D/g, '');
      updateField?.(name, digitsOnly);
      return;
    }
    updateField?.(name, value);
  };

  return (
    <FormBuilder
      layout="sections"
      sections={sections}
      values={formData}
      errors={errors}
      onFieldChange={handleFieldChange}
      disabled={disabled}
      renderField={renderUserField}
    />
  );
}

UserForm.propTypes = {
  formData: PropTypes.object,
  errors: PropTypes.object,
  updateField: PropTypes.func,
  validationAttempt: PropTypes.number,
  profileOptions: PropTypes.array,
  profileLoading: PropTypes.bool,
  onLoadMoreProfile: PropTypes.func,
  onSelectOrganisation: PropTypes.func,
  onProfileChange: PropTypes.func,
  disabled: PropTypes.bool,
  showOrganisationField: PropTypes.bool,
  disableProfileChange: PropTypes.bool,
  profileHelperText: PropTypes.string,
};
